import { jsonp } from 'vue-jsonp'
import { DownloadLinkInfo } from '../class/Downloads'
import {
  SectionMap,
  SectionItem,
  SectionList,
  SectionInfo
} from '../class/SectionList'

/**
 * URLをキーとしたページのマップ
 */
export type PageMap = Map<string, DownloadLinkInfo>

/**
 * PIMのidをキーとした対象データごとのページ（PDF）のマップ
 */
export type TargetPageMap = Map<number, PageMap>

/**
 * サイトをキーとした対象データごとのページ（PDF）のマップ
 */
export type SitePageMap = Map<string, TargetPageMap>

/**
 * PDFが取得可能な種別
 */
export type TargetType =
  | ''
  | 'product'
  | 'industry'
  | 'industry_category'
  | 'case'

// ---------------------------------------------------------------
// 定数
const PAGE_JSON_PATH = '/json/shimadzu_search/%SITE_CODE%/page_list.jsonp'
const PDF_JSON_PATH = '/json/shimadzu_search/%SITE_CODE%/pdf_list.jsonp'
const PRODUCT_JSON_PATH =
  '/json/shimadzu_search/%SITE_CODE%/product_download_list.jsonp'
const INDUSTRY_JSON_PATH =
  '/json/shimadzu_search/%SITE_CODE%/industry_download_list.jsonp'
const INDUSTRY_CATEGORY_JSON_PATH =
  '/json/shimadzu_search/%SITE_CODE%/industry_category_download_list.jsonp'
const CASE_JSON_PATH =
  '/json/shimadzu_search/%SITE_CODE%/case_download_list.jsonp'

// ---------------------------------------------------------------
// Main

export interface Site {
  code: string
  label: string
  url: string
  basePath: string
}

/**
 * ページ一覧取得クラス
 */
export class PageList {
  // --------------------------------------------------------------------------
  // local prop
  /**
   * 取得対象サイト
   */
  private _targetSites: Site[] = []
  /**
   * 取得結果（ページ）
   */
  private _resultMap: PageMap = new Map()
  /**
   * 取得結果（サイト）
   */
  private _resultSiteMap: SitePageMap = new Map()
  /**
   * セクションリスト
   */
  private _regionMap: SectionMap = new Map()
  private _sectionMap = new Map<string, SectionInfo>()

  constructor(sectionMap: SectionMap) {
    if (drupalSettings.vueComponent.sites === undefined) {
      throw Error('site settings undefined')
    }

    // 現在の情報を保持
    const currentSite = drupalSettings.vueComponent.sites.current

    // 親サイトの情報を保持
    const pageListSites = drupalSettings.vueComponent.sites.pageListSites

    this._targetSites.push(currentSite)
    for (const other of pageListSites) {
      this._targetSites.push(other)
    }

    // セクション情報を取得
    this._regionMap = sectionMap

    // セクション名のマップを取得
    for (const i of sectionMap.values()) {
      this._sectionMap.set(i.sectionCode, {
        sectionId: i.sectionId,
        sectionCode: i.sectionCode,
        sectionName: i.sectionName,
        sectionShortName: i.sectionShortName,
        type: i.type
      })
    }
  }

  // --------------------------------------------------------------------------
  // public method

  /**
   * 全てのPIM情報jsonを取得してMapに格納する。
   *- 親サイトを持つとき：親サイト + 自サイトのhtml,pdf情報
   *- 親サイトを持たないとき：自サイトのhtml,pdf情報
   */
  public async getPageListAll(): Promise<PageMap> {
    // 各リストが取得するまで待つ
    await Promise.all([this.getPageList(), this.getPdfList()]).catch((e) => {
      // エラーが発生した場合、リストを初期化
      this._resultMap = new Map()
      console.error(e)
    })

    // ページリストを返却
    return this._resultMap
  }

  /**
   * 全てのHTML情報をjsonから取得してMapに格納する。
   *- 親サイトを持つとき：親サイト + 自サイトのHTML情報
   *- 親サイトを持たないとき：自サイトのHTML情報
   */
  private async getPageList(): Promise<PageMap> {
    try {
      // 各リストが取得するまで待つ
      await Promise.all(
        this._targetSites.map((site) => this.setResultMap(PAGE_JSON_PATH, site))
      )
    } catch (e) {
      // エラーが発生した場合、リストを初期化
      this._resultMap = new Map()
      // console.error(e)
    }

    return this._resultMap
  }

  /**
   * 全てのPDF情報をjsonから取得してMapに格納する。
   *- 親サイトを持つとき：親サイト + 自サイトのPDF情報
   *- 親サイトを持たないとき：自サイトのPDF情報
   */
  private async getPdfList(): Promise<PageMap> {
    try {
      // 各リストが取得するまで待つ
      await Promise.all(
        this._targetSites.map((site) => this.setResultMap(PDF_JSON_PATH, site))
      )
    } catch (e) {
      // エラーが発生した場合、リストを初期化
      this._resultMap = new Map()
      // console.error(e)
    }

    return this._resultMap
  }

  /**
   * 全ての製品PDF情報をjsonから取得してMapに格納する。
   *- 親サイトを持つとき：自サイトの製品pdf情報
   *- 親サイトを持たないとき：自サイトの製品pdf情報
   */
  public async getTargetPageMap(targetType: TargetType): Promise<SitePageMap> {
    // 取得対象のJSONのパスを取得
    let jsonPath = ''
    switch (targetType) {
      case 'product':
        jsonPath = PRODUCT_JSON_PATH
        break
      case 'industry':
        jsonPath = INDUSTRY_JSON_PATH
        break
      case 'industry_category':
        jsonPath = INDUSTRY_CATEGORY_JSON_PATH
        break
      case 'case':
        jsonPath = CASE_JSON_PATH
        break

      default:
        throw new Error('The type to be retrieved is not specified.')
    }

    // 各リストが取得するまで待つ
    await Promise.all(
      this._targetSites.map((site) => this.setTargetResultMap(jsonPath, site))
    ).catch((e) => {
      // エラーが発生した場合、リストを初期化
      this._resultSiteMap = new Map()
      console.error(e)
    })

    return this._resultSiteMap
  }

  // --------------------------------------------------------------------------
  // private method

  /**
   *  DrupalSettingsからサイト情報を取得する。
   * @param jsonPath 取得するjsonのパス(定数:PAGE_JSON_PATH, PDF_JSON_PATH, PRODUCTS_JSON_PATH )
   * @param url 取得先のドメイン
   * @param siteCode 取得したいサイト
   */
  private getJsonURL(jsonPath: string, url: string, siteCode: string): string {
    // URLを作成し、返却
    return url.replace(/\/$/, '') + jsonPath.replace('%SITE_CODE%', siteCode)
  }

  /**
   * JSONPのコールバック関数名を取得する
   *
   * @param jsonPath
   *   JSONパス
   * @param siteCode
   *   サイトコード
   * @returns
   *   コールバック関数名
   */
  private getJsonCallback(jsonPath: string, siteCode: string): string {
    // ファイル名を取得
    const match = jsonPath.match(/([^/]+)\./)
    const filename = match && match[1] ? match[1] : ''

    // get + サイトコード + ファイル名 をコールバック関数名とする
    const callbackName = 'get_' + siteCode + '_' + filename

    // スネークケースをキャメルケースに変換
    return callbackName
      .split('_')
      .map(function (word, index) {
        if (index === 0) {
          return word.toLowerCase()
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
      .join('')
  }

  /**
   * ページ,PDF情報をjsonから取得してMapに格納する。
   * @param path JSONのパス
   * @param site 取得先のサイト
   */
  private async setResultMap(path: string, site: Site): Promise<void> {
    try {
      const jsonUrl = this.getJsonURL(path, site.url, site.code)
      const callbackName = this.getJsonCallback(path, site.code)

      const json = await jsonp(jsonUrl, { callbackName: callbackName }, 10000)
      this.jsonSetMap(json, site, this._resultMap)
    } catch (error) {
      // console.error(error)
      throw new Error(site.code + ' :Page list failed to retrieve.')
    }
  }

  /**
   * 製品ページ情報をjsonから取得してMapに格納する。
   * @param path JSONのパス
   * @param site 取得先のサイト
   * @param siteCode 取得したいサイト
   */
  private async setTargetResultMap(path: string, site: Site): Promise<void> {
    try {
      const jsonUrl = this.getJsonURL(path, site.url, site.code)
      const callbackName = this.getJsonCallback(path, site.code)

      const list = await jsonp(jsonUrl, { callbackName: callbackName }, 10000)

      // ターゲットごとにPDF情報を保存
      const resultTargetMap: TargetPageMap = new Map()
      for (const data of list) {
        const targetId = data.target_id as number
        const pdfList = data.pdf_list

        const map = new Map()
        this.jsonSetMap(pdfList, site, map)

        resultTargetMap.set(targetId, map)
      }

      // サイトごとにPDF情報を保存
      this._resultSiteMap.set(site.code, resultTargetMap)
    } catch (error) {
      throw new Error(site.code + ' :Page list failed to retrieve.')
    }
  }

  /**
   * ページ情報jsonを扱いやすいようMapに格納する。
   */
  private jsonSetMap(
    json: Record<string, unknown>[],
    site: Site,
    map: PageMap
  ): void {
    for (const item of json) {
      // JSONをObjectに変換
      const info = new DownloadLinkInfo(item)

      // サイトコードを補間
      info.siteCode = site.code

      // regionを補間
      // セクション情報が見つからない場合を考慮して、セクションコードに値があるかをフィルタする
      info.region = info.region
        .map(this.correctSectionItem, this)
        .filter((r) => r.sectionCode)

      // コンテンツタイプをセット
      info.setContentType()

      // URLをフルパス化
      info.url = this.getItemURL(site, info.url)

      // mapにセット
      map.set(info.url, info)
    }
  }

  /**
   * リンクJSONが持つセクション情報から正規の情報を補間する
   */
  private correctSectionItem(region: SectionItem): SectionItem {
    // サブカテゴリがOtherの場合、セクションコードを元に
    // サブカテゴリが指定されているデータと比較して、情報が足りない部分を補間して返却する
    if (region.subcategoryCode === 'other') {
      const section = this._sectionMap.get(region.sectionCode) ?? null
      if (section) {
        region.sectionId = section.sectionId
        region.sectionName = section.sectionName
        region.sectionShortName = section.sectionShortName
        region.type = section.type
        return region
      }
    }

    // セクション取得キーを作成
    const key = SectionList.getSectionKey(
      region.sectionCode,
      region.subcategoryCode
    )

    // サブカテゴリが指定されている場合、
    // セクション情報の一覧から一致する情報を返却する
    return this._regionMap.get(key) ?? new SectionItem()
  }

  /**
   * ドメイン付きのフルパスを作成する。
   *  @param site drupalSettings で設定しているsitesのURL
   *  @param itemPath jsonから取得したドメイン以下のページPath
   *  @return ドメイン付きのフルパス
   */
  private getItemURL(site: Site, itemPath: string): string {
    if (itemPath.match(/^http/)) {
      return itemPath
    }
    const regex = new RegExp('^' + site.basePath)
    return site.url.replace(/\/$/, '') + '/' + itemPath.replace(regex, '')
  }

  // --------------------------------------------------------------------------
  // getter / setter
  public get resultMap(): PageMap {
    return this._resultMap
  }
}
